import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Scale, Shield, KeyRound } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import LegalReviewPanel from '../components/dashboard/LegalReviewPanel';
import LegalAdvisorHub from '../components/legal/LegalAdvisorHub';
import { API_BASE as API } from '../lib/apiBase';
import { toast } from 'sonner';

export default function LegalAdvisorPage() {
  const { user, token, loading, refreshUser } = useAuth();
  const { lang } = useLang();
  const headers = useMemo(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token]
  );
  const [session, setSession] = useState({ active: false, access_configured: false });
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchSession = useCallback(async () => {
    if (!token || user?.role !== 'legal_advisor') return;
    try {
      const res = await fetch(`${API}/api/auth/legal-advisor/session`, { headers });
      if (res.ok) setSession(await res.json());
    } catch {
      /* ignore */
    }
  }, [token, user?.role, headers]);

  useEffect(() => {
    if (loading || user?.role !== 'legal_advisor') return;
    (async () => {
      await refreshUser();
      await fetchSession();
    })();
  }, [loading, user?.role, fetchSession, refreshUser]);

  const verifyCode = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/auth/legal-advisor/verify`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ access_code: code.trim() }),
      });
      const err = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(err.detail || (lang === 'es' ? 'Código incorrecto' : 'Invalid code'));
        setSubmitting(false);
        return;
      }
      setCode('');
      toast.success(lang === 'es' ? 'Acceso concedido' : 'Access granted');
      await refreshUser();
      await fetchSession();
    } catch {
      toast.error(lang === 'es' ? 'Error de red' : 'Network error');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center text-pulse-muted text-sm">
        {lang === 'es' ? 'Cargando…' : 'Loading…'}
      </div>
    );
  }

  if (!user) {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <Scale className="w-12 h-12 mx-auto text-blue-400 mb-4" />
        <h1 className="font-heading font-bold text-xl text-white mb-2">
          {lang === 'es' ? 'Panel asesor legal' : 'Legal advisor panel'}
        </h1>
        <p className="text-pulse-muted text-sm mb-6">
          {lang === 'es'
            ? 'Inicia sesión con la cuenta que te haya invitado el equipo.'
            : 'Sign in with the account the team invited.'}
        </p>
        <Link
          to="/login"
          className="inline-flex items-center justify-center rounded-xl bg-pulse-primary text-black font-semibold px-6 py-3 text-sm"
        >
          {lang === 'es' ? 'Ir a inicio de sesión' : 'Go to login'}
        </Link>
      </div>
    );
  }

  if (user.role !== 'legal_advisor') {
    return (
      <div className="max-w-lg mx-auto px-4 py-16 text-center">
        <Shield className="w-12 h-12 mx-auto text-amber-400 mb-4" />
        <h1 className="font-heading font-bold text-xl text-white mb-2">
          {lang === 'es' ? 'Sin permiso' : 'No access'}
        </h1>
        <p className="text-pulse-muted text-sm">
          {lang === 'es'
            ? 'Esta ruta es solo para cuentas con rol «asesor legal».'
            : 'This route is only for accounts with the legal advisor role.'}
        </p>
      </div>
    );
  }

  const sessionActive = Boolean(user.legal_advisor_session_active || session.active);
  const accessConfigured = session.access_configured;

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Scale size={28} className="text-blue-400" />
          <h1 className="font-heading font-bold text-2xl text-white">
            {lang === 'es' ? 'Revisión legal' : 'Legal review'}
          </h1>
        </div>
        <p className="text-pulse-muted text-sm">
          {lang === 'es'
            ? 'Vista orientativa para asesoría externa. No sustituye asesoramiento jurídico vinculante.'
            : 'Advisory view for external counsel. Not a substitute for binding legal advice.'}
        </p>
      </div>

      {accessConfigured && !sessionActive && (
        <form
          onSubmit={verifyCode}
          className="mb-8 rounded-2xl border border-pulse-border bg-pulse-surface/80 p-6 flex flex-col sm:flex-row gap-4 items-end"
        >
          <div className="flex-1 w-full">
            <label className="flex items-center gap-2 text-xs text-pulse-muted mb-2">
              <KeyRound size={14} />
              {lang === 'es' ? 'Código de acceso del panel' : 'Panel access code'}
            </label>
            <input
              type="password"
              autoComplete="off"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder={lang === 'es' ? 'Introduce el código' : 'Enter code'}
              className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-4 py-3 text-sm"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:w-auto shrink-0 bg-pulse-primary text-black font-semibold px-6 py-3 rounded-xl text-sm disabled:opacity-50"
          >
            {submitting ? '…' : lang === 'es' ? 'Desbloquear' : 'Unlock'}
          </button>
        </form>
      )}

      {accessConfigured && sessionActive && (
        <div className="space-y-8">
          <LegalAdvisorHub token={token} />
          <div className="border-t border-pulse-border pt-8">
            <h2 className="text-sm font-semibold text-pulse-muted uppercase tracking-wider mb-3">
              {lang === 'es' ? 'Panel de revisión fiscal (resumen previo)' : 'Fiscal review panel (summary)'}
            </h2>
            <LegalReviewPanel advisorActions />
          </div>
        </div>
      )}

      {!accessConfigured && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200">
          {lang === 'es'
            ? 'El código de acceso no está configurado en el servidor. Contacta con el equipo técnico.'
            : 'Access code is not configured on the server. Contact the technical team.'}
        </div>
      )}
    </div>
  );
}
